import { useLayoutEffect } from "react";
import { LIST_HEADER_RADIUS } from "./listPresentation.js";

// Corner geometry only. In particular, never set overflow on a sticky ancestor.
export const TABLE_TOP_CORNERS = Object.freeze({
  borderTopLeftRadius: LIST_HEADER_RADIUS,
  borderTopRightRadius: LIST_HEADER_RADIUS,
});
export const TABLE_HEADER_CLIP = Object.freeze({
  ...TABLE_TOP_CORNERS,
  clipPath: `inset(0 round ${LIST_HEADER_RADIUS}px ${LIST_HEADER_RADIUS}px 0 0)`,
});

export function headerViewportClip(header, viewport, edge = "both") {
  const left = Math.max(0, viewport.left - header.left);
  const right = Math.max(0, header.right - viewport.right);
  const leftRadius = edge === "right" ? 0 : LIST_HEADER_RADIUS;
  const rightRadius = edge === "left" ? 0 : LIST_HEADER_RADIUS;
  return `inset(0px ${right}px 0px ${left}px round ${leftRadius}px ${rightRadius}px 0px 0px)`;
}

// Explicit table owners opt in through ListOverflowScope or CornerTable. The
// clip is on the header only, not the table/body: drawer floating layers remain
// free, and a wide header gets rounded at the viewport edges, not offscreen.
export function attachTableHeaderCorners(table) {
  if (table?.hasAttribute("data-table-header-corners")) return () => {};
  const head = table?.tHead;
  if (!head || head.getAttribute("aria-hidden") === "true"
    || head.hasAttribute("inert") || head.style.visibility === "collapse") return () => {};
  const boundary = table.parentElement;
  if (!boundary) return () => {};
  const saved = [];
  const assign = (node, values) => {
    for (const [key, value] of Object.entries(values)) {
      saved.push([node, key, node.style[key]]);
      node.style[key] = value;
    }
  };
  const radius = `${LIST_HEADER_RADIUS}px`;
  const corners = { borderTopLeftRadius: radius, borderTopRightRadius: radius };
  assign(table, corners);
  const oldRadius = table.style.getPropertyValue("--list-header-radius");
  table.style.setProperty("--list-header-radius", radius);
  assign(head, { ...corners, clipPath: head.style.clipPath });
  // Only a direct, unpadded table viewport owns this perimeter. A padded panel
  // or a section heading is not the table boundary and must not be restyled.
  const style = getComputedStyle(boundary);
  const localViewport = /auto|scroll|hidden|clip/.test(style.overflowX)
    && !parseFloat(style.paddingLeft) && !parseFloat(style.paddingRight);
  if (localViewport) assign(boundary, corners);
  const update = () => {
    const rect = table.getBoundingClientRect();
    const view = boundary.getBoundingClientRect();
    // The rowgroup may itself be sticky; only horizontal bounds participate.
    head.style.clipPath = headerViewportClip(rect, localViewport ? {
      left: view.left + boundary.clientLeft,
      right: view.left + boundary.clientLeft + boundary.clientWidth,
    } : rect);
  };
  update();
  boundary.addEventListener("scroll", update, { passive: true });
  const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
  observer?.observe(table);
  observer?.observe(boundary);
  window.addEventListener("resize", update);
  table.dataset.tableHeaderCorners = "";
  return () => {
    observer?.disconnect();
    boundary.removeEventListener("scroll", update);
    window.removeEventListener("resize", update);
    delete table.dataset.tableHeaderCorners;
    if (oldRadius) table.style.setProperty("--list-header-radius", oldRadius);
    else table.style.removeProperty("--list-header-radius");
    for (const [node, key, value] of saved.reverse()) node.style[key] = value;
  };
}

export function useTableHeaderCorners(table) {
  useLayoutEffect(() => attachTableHeaderCorners(table), [table]);
}

export function useGridHeaderCorners(headerRef, viewportRef, edge = "both") {
  useLayoutEffect(() => {
    const header = headerRef.current;
    const viewport = viewportRef.current;
    return attachGridHeaderCorners(header, viewport, edge);
  }, [headerRef, viewportRef, edge]);
}

export function attachGridHeaderCorners(header, viewport, edge = "both") {
  if (!header || !viewport) return undefined;
  const previous = [header.style.borderTopLeftRadius, header.style.borderTopRightRadius, header.style.clipPath];
  const viewportCorners = [viewport.style.borderTopLeftRadius, viewport.style.borderTopRightRadius];
  header.style.borderTopLeftRadius = viewport.style.borderTopLeftRadius = `${edge === "right" ? 0 : LIST_HEADER_RADIUS}px`;
  header.style.borderTopRightRadius = viewport.style.borderTopRightRadius = `${edge === "left" ? 0 : LIST_HEADER_RADIUS}px`;
  const update = () => {
    const rect = viewport.getBoundingClientRect();
    header.style.clipPath = headerViewportClip(header.getBoundingClientRect(), {
      left: rect.left + viewport.clientLeft,
      right: rect.left + viewport.clientLeft + viewport.clientWidth,
    }, edge);
  };
  update();
  viewport.addEventListener("scroll", update, { passive: true });
  const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
  observer?.observe(header); observer?.observe(viewport);
  window.addEventListener("resize", update);
  return () => {
    observer?.disconnect();
    viewport.removeEventListener("scroll", update);
    window.removeEventListener("resize", update);
    [header.style.borderTopLeftRadius, header.style.borderTopRightRadius, header.style.clipPath] = previous;
    [viewport.style.borderTopLeftRadius, viewport.style.borderTopRightRadius] = viewportCorners;
  };
}

export function useTableHeaderCornerScope(root) {
  useLayoutEffect(() => {
    if (!root) return undefined;
    const attached = new Map();
    const discover = () => {
      for (const [table, cleanup] of attached) {
        if (!root.contains(table)) { cleanup(); attached.delete(table); }
      }
      for (const table of root.querySelectorAll("table")) {
        if (table.closest("[data-list-scope]") !== root
          || table.hasAttribute("data-table-header-corners") || attached.has(table)
          || !table.tHead) continue;
        attached.set(table, attachTableHeaderCorners(table));
      }
    };
    discover();
    const observer = new MutationObserver(discover);
    observer.observe(root, { childList: true, subtree: true });
    return () => { observer.disconnect(); attached.forEach(cleanup => cleanup()); };
  }, [root]);
}
